/**
 * Users Management Page
 * 
 * Main page for managing users with features:
 * - User list with pagination
 * - Create/Edit/Delete users
 * - Status management
 * 
 * Uses StandardPage template for consistent layout
 */

import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus } from 'react-icons/fi';
import { useCrudPage } from '@/hooks';
import { userService } from '@/services';
import StandardPage from '@/components/Common/StandardPage';
import UserTable from '@/components/UserTable/UserTable';
import Modal from '@/components/Common/Modal';
import UserForm from '@/components/Forms/UserForm';
import type { UserFormData } from '@/components/Forms/UserForm';
import type { UserListItem } from '@/types';

const UsersPage: FC = () => {
  const navigate = useNavigate();

  /**
   * Use CRUD page hook
   */
  const {
    data: users,
    total,
    isLoading,
    isError,
    error,
    pagination,
    setPagination,
    isModalOpen,
    modalMode,
    selectedItem: selectedUser,
    mutations: { create: createMutation, update: updateMutation },
    refetch,
    handleOpenCreate,
    handleOpenEdit,
    handleCancelModal,
  } = useCrudPage({
    queryKey: 'users',
    service: {
      getList: (filters: any) =>
        userService.getUsers(
          {
            page: filters.page,
             size: filters.size,
             sort: filters.sortBy && filters.sortOrder ? `${filters.sortBy},${filters.sortOrder}` : undefined,

          },
          {}
        ),
      create: userService.createUser,
      update: userService.updateUser,
      delete: userService.deleteUser,
    },
    initialPageSize: 10,
    successMessages: {
      create: {
        title: 'Usuário criado!',
        message: 'O usuário foi criado com sucesso.',
      },
      update: {
        title: 'Usuário atualizado!',
        message: 'As alterações foram salvas com sucesso.',
      },
      delete: {
        title: 'Usuário deletado!',
        message: 'O usuário foi removido com sucesso.',
      },
    },
  });

  /**
   * Handle view user detail
   */
  const handleView = useCallback(
    (user: UserListItem) => {
      navigate(`/users/${user.ref}`);
    },
    [navigate]
  );

  /**
   * Handle form submit
   */
  const handleFormSubmit = useCallback(
    async (data: UserFormData) => {
      if (modalMode === 'create') {
        await createMutation.mutateAsync(data);
      } else if (selectedUser) {
        await updateMutation.mutateAsync({
          id: selectedUser.id,
          data,
        });
      }
    },
    [modalMode, selectedUser, createMutation, updateMutation]
  );

  return (
    <>
      <StandardPage
        header={{
          title: 'Gerenciamento de Usuários',
          subtitle: 'Gerencie os usuários do sistema',
          actions: [
            {
              label: 'Novo Usuário',
              icon: <FiPlus size={18} />,
              onClick: handleOpenCreate,
              variant: 'primary',
            },
          ],
        }}
        isLoading={isLoading}
        isError={isError}
        error={error}
        onRefetch={refetch}
        pagination={{
          state: pagination,
          data: {
            total,
            totalPages: Math.ceil(total / pagination.pageSize),
            page: pagination.pageIndex,
            pageSize: pagination.pageSize,
          },
          onChange: setPagination,
        }}
      >
        <UserTable
          users={users}
          loading={false}
          onEdit={handleOpenEdit}
          onView={handleView}
        />
      </StandardPage>

      {/* User Modal (Create/Edit) */}
      <Modal
        open={isModalOpen}
        onClose={handleCancelModal}
        title={modalMode === 'create' ? 'Criar Novo Usuário' : 'Editar Usuário'}
        description={
          modalMode === 'create'
            ? 'Preencha os dados para criar um novo usuário'
            : 'Atualize as informações do usuário'
        }
        size="md"
      >
        <UserForm
          mode={modalMode}
          initialValues={selectedUser || undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleCancelModal}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      </Modal>
    </>
  );
};

export default UsersPage;
